import { format, formatDistanceToNowStrict } from "date-fns";
import { apiBaseUrl } from "../constants/env.constants";
import { USER_LOGOUT } from "../constants";
import { LAUNCH_DATE_LOCK_HOUR } from "../constants/AdLaunch";

export const formatDisplayDate = (dateInput) => {
  if (dateInput == null) return null;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, "d MMM ''yy");
};

export const formatDisplayDateTime = (dateInput) => {
  if (dateInput == null) return null;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, "d MMM ''yy, h:mm a");
};

/** "3h ago" / "5m ago" — for notes, where recency reads better than a clock time. */
export const formatRelativeTime = (dateInput) => {
  if (dateInput == null) return null;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  return `${formatDistanceToNowStrict(d)} ago`;
};

export const formatSeconds = (s) => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const total = Math.floor(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

const logoutUrl = apiBaseUrl.replace(/\/$/, "") + USER_LOGOUT;

const handleLogout = async ({ type, setIsLoading } = {}) => {
  try {
    await fetch(logoutUrl, { method: "POST", credentials: "include" });
  } catch (e) {
    if (type !== "refresh_token" && process.env.NODE_ENV === "development") {
      console.error("Logout API failed", e);
    }
  }
  if (setIsLoading) setIsLoading(false);
  localStorage.clear();
  window.location.href = "/login";
};

const PLACEHOLDER_AVATAR =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><circle fill="%236b7280" cx="64" cy="64" r="64"/><circle fill="%239ca3af" cx="64" cy="52" r="24"/><path fill="%239ca3af" d="M24 128c0-22 18-40 40-40s40 18 40 40H24z"/></svg>'
  );

// Default profile avatar
const handleDefaultProfile = ({ userName, avatar }) => {
  if (avatar) return avatar;
  if (!userName) return PLACEHOLDER_AVATAR;
  const parts = String(userName).trim().split(/\s+/).filter(Boolean);
  const raw =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (userName[0] || "?").toUpperCase();
  const initial = raw.replace(/[^A-Za-z0-9]/g, "?").slice(0, 2) || "?";
  const palette = [
    "6366f1",
    "8b5cf6",
    "ec4899",
    "f43f5e",
    "f97316",
    "eab308",
    "22c55e",
    "14b8a6",
  ];
  const idx = initial.charCodeAt(0) % palette.length;
  const bg = palette[idx];
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">' +
    `<circle fill="#${bg}" cx="64" cy="64" r="64"/>` +
    `<text x="64" y="78" font-family="system-ui,sans-serif" font-size="48" fill="white" text-anchor="middle">${initial}</text>` +
    "</svg>";
  return "data:image/svg+xml," + encodeURIComponent(svg);
};

const handleFormatDate = ({ dateInString, showTime = true }) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (!dateInString) {
    return "-";
  }

  const dateused = new Date(Date.parse(dateInString));
  const month = months[dateused.getMonth()];
  const date = dateused.getDate();
  const year = dateused.getFullYear();
  const hours = dateused.getHours();
  let mins = dateused.getMinutes();
  const formattedDate = date < 10 ? `0${date}` : date;
  mins = mins < 10 ? `0${mins}` : mins;
  return `${formattedDate} ${month} ${year} ${
    showTime ? `at ${hours}:${mins}` : ""
  }`;
};

const handlePageChange = ({ value, location, navigate, setPageNumber }) => {
  if (setPageNumber) setPageNumber(value);
  handleAppendQueryToUrl({
    location,
    navigate,
    updateParams: [{ queryName: "page", queryValue: value }],
  });
};

const handleAppendQueryToUrl = ({
  location,
  navigate,
  updateParams,
  deleteParams = [],
}) => {
  const searchParams = new URLSearchParams(location?.search);
  updateParams?.forEach(({ queryName, queryValue }) => {
    searchParams?.set(queryName, queryValue);
  });
  // Delete specified keys
  deleteParams.forEach((queryName) => {
    searchParams.delete(queryName);
  });
  navigate(`${location.pathname}?${searchParams.toString()}`);
};

// Normalizes a permission/code string: trims whitespace, collapses internal
// spaces to a single underscore, and lowercases (e.g. "User  Manage" → "user_manage").
const normalizeCode = (value) => {
  if (value == null || typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, "_").toLowerCase();
};

// Today's date (yyyy-MM-dd) and hour in IST, whatever the browser's timezone.
const handleGetIstNow = () => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value])
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) };
};

/** Mirrors the backend: locked when unset, after 11 PM IST on the launch day, or overdue and already launched. */
const handleGetLaunchDateWindow = ({ launchDate, launched = false }) => {
  const now = handleGetIstNow();
  const afterLock = now.hour >= LAUNCH_DATE_LOCK_HOUR;
  const tomorrow = new Date(`${now.date}T00:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const earliest = afterLock ? tomorrow.toISOString().slice(0, 10) : now.date;
  const lockReason = !launchDate
    ? "notSet"
    : launchDate === now.date && afterLock
      ? "launchesTonight"
      : launchDate < now.date && launched
        ? "launched"
        : null;
  return { editable: !lockReason, earliest, lockReason };
};

/** Auto launch from a bulk cell: only an explicit No/false turns it off; blank means on. */
const handleParseAutoLaunch = ({ value }) =>
  !["no", "false", "0", "off", "n"].includes(String(value ?? "").trim().toLowerCase());

export {
  handleLogout,
  handleDefaultProfile,
  handleFormatDate,
  handlePageChange,
  handleAppendQueryToUrl,
  normalizeCode,
  handleGetIstNow,
  handleGetLaunchDateWindow,
  handleParseAutoLaunch,
};
